import { useEffect, useState, useRef, useCallback } from "react";
import { Container, TextField, Button, Grid, Typography, ButtonGroup, CardHeader, Autocomplete, Checkbox, Box, Chip } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";
import { Countries } from "../utils/Lookup";
import { Icons } from "../utils/Icon";
import { GroupHeader, GroupItems, CustomStyles } from "../utils/Styled";

import axios from "axios";
import moment from "moment";
import MaterialTable from "material-table";
import { useNavigate } from "react-router-dom";

import CreateOrganizationForm from "../components/forms/organizations/Create";
import EditOrganizationForm from "../components/forms/organizations/Edit";
import ViewOrganizationForm from "../components/forms/organizations/View";
import DeleteOrganizationForm from "../components/forms/organizations/Delete";
import ImportOrganizationForm from "../components/forms/organizations/Import";
import OrganizationContractsForm from "../components/forms/organizations/Contracts";
import OrganizationTouchpointsForm from "../components/forms/organizations/Touchpoints";

import CountrySelect from "../components/common/CountrySelect";

const DEFAULT_DORMANT_MONTHS = 6;
const DEFAULT_MIN_NET_SALES = 30000;

const formatLastSynced = (value) => {
    if (!value) return null;

    const parsed = moment(value);
    if (!parsed.isValid()) return null;

    return parsed.subtract(8, "hours").format("DD/MM/YYYY hh:mm A");
};

const liveSyncChipLabel = (lastSyncedAt) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "success.main", flexShrink: 0 }} />
        <span>{`Live sync from Million Cloud Database · 15 minute cache @Last synced: ${lastSyncedAt}`}</span>
    </Box>
);

export const OrganizationsPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();

    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const codeInputRef = useRef(null);
    const nameInputRef = useRef(null);
    const emailInputRef = useRef(null);
    const officePhoneInputRef = useRef(null);
    const picNameInputRef = useRef(null);
    const businessTypeInputRef = useRef(null);

    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [filter, setFilter] = useState({ country_id: identity.user.country_id });
    const [sort, setSort] = useState({});
    const [lastSyncedAt, setLastSyncedAt] = useState(null);

    const [userCountry, setUserCountry] = useState([]);

    useEffect(() => {
        async function fetchCountryData() {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/users/access-country`);
            setUserCountry(response.data.data.countries);
        }
        fetchCountryData();
    }, []);

    const [productsData, setProductsData] = useState([]);
    const [businessTypeData, setBusinessTypeData] = useState([]);
    const [businessTypeInput, setBusinessTypeInput] = useState("");
    const [businessTypeAutocompleteOpen, setBusinessTypeAutocompleteOpen] = useState(false);
    const [repurchaseSummary, setRepurchaseSummary] = useState({
        totalRepurchased: 0,
        repurchaseDealers: 0,
    });
    const [newCustomerSummary, setNewCustomerSummary] = useState({
        newEndUsersCount: 0,
        newDealersCount: 0,
    });
    const [dormantSummary, setDormantSummary] = useState({
        dormantEndUsersCount: 0,
        dormantDealersCount: 0,
    });
    const [continuousActiveSummary, setContinuousActiveSummary] = useState({
        total5YLoyalBase: 0,
    });
    const [netSalesSummary, setNetSalesSummary] = useState({
        totalCount: 0,
    });

    const [open, setOpen] = useState({
        create: false,
        edit: false,
        view: false,
        import: false,
        contracts: false,
        touchpoints: false,
    });
    const [selectedRow, setSelectedRow] = useState({});

    const customerStats = [
        { label: "Repurchase Customers", value: Number(repurchaseSummary.totalRepurchased || 0).toLocaleString("en-US"), route: "/organizations/repurchase-customers-analysis" },
        { label: "Repurchase Dealers", value: Number(repurchaseSummary.repurchaseDealers || 0).toLocaleString("en-US"), route: "/organizations/repurchase-customers-analysis" },
        {
            label: "5Y Continuous Active Customers",
            value: Number(continuousActiveSummary.total5YLoyalBase || 0).toLocaleString("en-US"),
            route: "/organizations/continuous-active-customers-analysis",
        },
        { label: "Net Sales > 30k", value: Number(netSalesSummary.totalCount || 0).toLocaleString("en-US"), route: "/organizations/net-sales-customers" },
        { label: "New Customers", value: Number(newCustomerSummary.newEndUsersCount || 0).toLocaleString("en-US"), route: "/organizations/new-customers-analysis" },
        { label: "New Dealers", value: Number(newCustomerSummary.newDealersCount || 0).toLocaleString("en-US"), route: "/organizations/new-customers-analysis" },
        { label: "Dormant Customers", value: Number(dormantSummary.dormantEndUsersCount || 0).toLocaleString("en-US"), route: "/organizations/dormant-customers-analysis" },
        { label: "Dormant Dealers", value: Number(dormantSummary.dormantDealersCount || 0).toLocaleString("en-US"), route: "/organizations/dormant-customers-analysis" },
    ];
    const hasSelectedCountry = !!filter.country_id && String(filter.country_id).toLowerCase() !== "all";

    const handleOpen = (type, row) => {
        setOpen({ ...open, [type]: true });
        setSelectedRow(row);
    };

    const handleClose = (type) => {
        setOpen({ ...open, [type]: false });
        setSelectedRow(null);
    };

    const handleResetFilter = () => {
        setPage(0);
        setBusinessTypeInput("");
        setBusinessTypeAutocompleteOpen(false);
        setFilter({ country_id: identity.user.country_id });
    };

    const handleFilterChange = (event) => {
        setPage(0);
        setFilter({ ...filter, [event.target.name]: event.target.value });
        setFilter((prevFilter) => {
            if (prevFilter.country_id !== filter.country_id) {
                return { ...prevFilter, product_id: [] };
            }
            return prevFilter;
        });
    };

    const handleProductFilterChange = (selectedValue) => {
        setPage(0);
        setFilter({ ...filter, product_id: selectedValue });
    };

    const handleBusinessTypeFilterChange = (newInputValue) => {
        setPage(0);
        setBusinessTypeInput(newInputValue ? newInputValue.toUpperCase() : "");
    };

    const downloadExcelFile = () => {
        const now = moment().format("YYYY-MM-DD_HH-mm-ss");
        const fileName = `Organizations_${now}.xlsx`;

        let customFilter = { ...filter };

        if (customFilter.product_id && customFilter.product_id.length > 0) customFilter.product_id = customFilter.product_id.map((v) => v.product_id);

        axios({
            url: process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/organizations/export",
            method: "GET",
            responseType: "blob", // important
            params: {
                filter: JSON.stringify(customFilter),
            },
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
        });
    };

    const fetchData = useCallback(async () => {
        try {
            let customFilter = { ...filter };

            if (customFilter.product_id && customFilter.product_id.length > 0) customFilter.product_id = customFilter.product_id.map((v) => v.product_id);

            const { data } = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/organizations`, {
                params: {
                    page: page + 1,
                    perPage,
                    filter: JSON.stringify(customFilter),
                    sort,
                },
            });
            setData(data.data.organizations);
            setCount(data.data.total);
        } catch (error) {
            if (error.response.status === 401) {
                console.log("Unauthorized: ", error);
                logout("Your account has been logged out of a session.");
            } else {
                console.log(error);
            }
        }
    }, [page, perPage, filter, sort, logout]);

    const fetchSyncStatus = useCallback(async () => {
        const selectedCountryId = filter.country_id;
        if (!selectedCountryId || String(selectedCountryId).toLowerCase() === "all") {
            setLastSyncedAt(null);
            return;
        }

        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/organizations/sync-status", {
                params: {
                    country_id: selectedCountryId,
                },
            });
            const syncAt =
                response?.data?.data?.state?.last_successful_sync_at ??
                response?.data?.data?.last_successful_sync_at ??
                response?.data?.last_successful_sync_at;
            setLastSyncedAt(formatLastSynced(syncAt));
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            setLastSyncedAt(null);
        }
    }, [logout, filter.country_id]);

    const fetchRepurchaseSummary = useCallback(async () => {
        const selectedCountryId = filter.country_id;
        if (!selectedCountryId || String(selectedCountryId).toLowerCase() === "all") {
            setRepurchaseSummary({
                totalRepurchased: 0,
                repurchaseDealers: 0,
            });
            return;
        }

        const now = moment();
        const month = now.month() + 1;
        const year = now.year();

        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${selectedCountryId}/monthly-repurchase-breakdown`, {
                params: {
                    from_month: month,
                    from_year: year,
                    to_month: month,
                    to_year: year,
                },
            });

            const summary = response?.data?.data?.totalSummary || {};
            setRepurchaseSummary({
                totalRepurchased: summary.totalRepurchased || 0,
                repurchaseDealers: summary.repurchaseDealers || 0,
            });
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            setRepurchaseSummary({
                totalRepurchased: 0,
                repurchaseDealers: 0,
            });
        }
    }, [logout, filter.country_id]);

    const fetchNewCustomerSummary = useCallback(async () => {
        const selectedCountryId = filter.country_id;
        if (!selectedCountryId || String(selectedCountryId).toLowerCase() === "all") {
            setNewCustomerSummary({
                newEndUsersCount: 0,
                newDealersCount: 0,
            });
            return;
        }

        const now = moment();
        const month = now.month() + 1;
        const year = now.year();

        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${selectedCountryId}/monthly-new-customer-breakdown`, {
                params: {
                    from_month: month,
                    from_year: year,
                    to_month: month,
                    to_year: year,
                },
            });

            const summary = response?.data?.data?.totalSummary || {};
            setNewCustomerSummary({
                newEndUsersCount: summary.newEndUsersCount || 0,
                newDealersCount: summary.newDealersCount || 0,
            });
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            setNewCustomerSummary({
                newEndUsersCount: 0,
                newDealersCount: 0,
            });
        }
    }, [logout, filter.country_id]);

    const fetchDormantSummary = useCallback(async () => {
        const selectedCountryId = filter.country_id;
        if (!selectedCountryId || String(selectedCountryId).toLowerCase() === "all") {
            setDormantSummary({
                dormantEndUsersCount: 0,
                dormantDealersCount: 0,
            });
            return;
        }

        const now = moment();
        const month = now.month() + 1;
        const year = now.year();

        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${selectedCountryId}/monthly-dormant-customer-breakdown`, {
                params: {
                    from_month: month,
                    from_year: year,
                    to_month: month,
                    to_year: year,
                    dormant_months: DEFAULT_DORMANT_MONTHS,
                },
            });

            const summary = response?.data?.data?.totalSummary || {};
            setDormantSummary({
                dormantEndUsersCount: summary.dormantEndUsersCount || 0,
                dormantDealersCount: summary.dormantDealersCount || 0,
            });
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            setDormantSummary({
                dormantEndUsersCount: 0,
                dormantDealersCount: 0,
            });
        }
    }, [logout, filter.country_id]);

    const fetchContinuousActiveSummary = useCallback(async () => {
        const selectedCountryId = filter.country_id;
        if (!selectedCountryId || String(selectedCountryId).toLowerCase() === "all") {
            setContinuousActiveSummary({
                total5YLoyalBase: 0,
            });
            return;
        }

        const now = moment();
        const toYear = now.year();
        const fromYear = toYear;

        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${selectedCountryId}/yearly-continuous-active-customers`, {
                params: {
                    from_year: fromYear,
                    to_year: toYear,
                    years: 5,
                },
            });

            const summary = response?.data?.data?.totalSummary || {};
            setContinuousActiveSummary({
                total5YLoyalBase: summary.total5YLoyalBase || 0,
            });
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            setContinuousActiveSummary({
                total5YLoyalBase: 0,
            });
        }
    }, [logout, filter.country_id]);

    const fetchNetSalesSummary = useCallback(async () => {
        const selectedCountryId = filter.country_id;
        if (!selectedCountryId || String(selectedCountryId).toLowerCase() === "all") {
            setNetSalesSummary({
                totalCount: 0,
            });
            return;
        }

        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${selectedCountryId}/net-sales-customers`, {
                params: {
                    min_net_sales: DEFAULT_MIN_NET_SALES,
                    limit: 1,
                },
            });

            const summary = response?.data?.data?.summary || {};
            const total = response?.data?.data?.total;
            setNetSalesSummary({
                totalCount: Number(summary.totalCount ?? total ?? 0),
            });
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            setNetSalesSummary({
                totalCount: 0,
            });
        }
    }, [logout, filter.country_id]);

    useEffect(() => {
        fetchData();
    }, [fetchData, page, perPage, filter, sort]);

    useEffect(() => {
        fetchSyncStatus();
    }, [fetchSyncStatus]);

    useEffect(() => {
        fetchRepurchaseSummary();
    }, [fetchRepurchaseSummary]);

    useEffect(() => {
        fetchNewCustomerSummary();
    }, [fetchNewCustomerSummary]);

    useEffect(() => {
        fetchDormantSummary();
    }, [fetchDormantSummary]);

    useEffect(() => {
        fetchContinuousActiveSummary();
    }, [fetchContinuousActiveSummary]);

    useEffect(() => {
        fetchNetSalesSummary();
    }, [fetchNetSalesSummary]);

    useEffect(() => {
        axios
            .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/products/select?country=${filter.country_id}`)
            .then((response) => {
                setProductsData(response.data.data.products);
            })
            .catch((error) => console.log(error));
    }, [filter.country_id]);

    useEffect(() => {
        const debounceTimer = setTimeout(async () => {
            const search = (businessTypeInput || "").trim();

            setFilter((prevFilter) => {
                if ((prevFilter.business_type || "") === search) {
                    return prevFilter;
                }

                return {
                    ...prevFilter,
                    business_type: search,
                };
            });

            try {
                const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/organizations/get-business-type?search=${search}`);
                setBusinessTypeData(response.data.data.texts);
            } catch (error) {
                console.error(error);
            }
        }, 250);

        return () => clearTimeout(debounceTimer);
    }, [businessTypeInput]);

    const columns = [
        {
            title: "Code",
            field: "org_code",
            cellStyle: { width: 110, maxWidth: 110 },
            headerStyle: { width: 110, maxWidth: 110 },
        },
        { title: "Name", field: "org_name", width: "80%" },
        { title: "Email", field: "email" },
        { title: "Office Phone", field: "office_phone" },
        { title: "Mobile Phone", field: "mobile_phone" },
    ];

    const actions = [];

    actions.push({
        icon: () => <Icons.Summarize />,
        tooltip: "View Organization Contracts",
        onClick: (event, rowData) => handleOpen("contracts", rowData),
    });

    actions.push({
        icon: () => <Icons.PhoneForwardedTwoToneIcon />,
        tooltip: "View Customer Touchpoints",
        onClick: (event, rowData) => handleOpen("touchpoints", rowData),
    });

    if (identity.user.role_id === 1 || identity.user.role_id === 2) {
        actions.push({
            icon: () => <Icons.Dashboard />,
            tooltip: "View Customer Dashboard",
            onClick: (event, rowData) => navigate(`/organizations/${rowData.org_id}/dashboard`),
        });
        actions.push({
            icon: () => <Icons.Edit />,
            tooltip: "Edit Organization Information",
            onClick: (event, rowData) => handleOpen("edit", rowData),
        });
        actions.push({
            icon: () => <Icons.Delete />,
            tooltip: "Delete Organization",
            onClick: (event, rowData) => DeleteOrganizationForm(rowData, fetchData),
        });
    } else {
        actions.push({
            icon: () => <Icons.Preview />,
            tooltip: "View Organization Information",
            onClick: (event, rowData) => handleOpen("view", rowData),
        });
    }

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <MaterialTable
                    title="Organization List"
                    icons={Icons}
                    columns={columns}
                    data={data}
                    page={page}
                    totalCount={count}
                    style={{ padding: "24px" }}
                    options={{
                        pageSize: perPage,
                        pageSizeOptions: [10, 20, 30, 50],
                        search: false,
                        filtering: false,
                        sorting: false,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "600px",
                        paginationType: "stepped",
                        padding: "dense",
                        draggable: false,
                        rowStyle: (rowData) => ({
                            backgroundColor: selectedRow === rowData.tableData.id ? "#EEE" : "#FFF",
                        }),
                    }}
                    onRowClick={(evt, selectedRow) => setSelectedRow(selectedRow.tableData.id)}
                    localization={{
                        pagination: {
                            labelRowsPerPage: "",
                            labelDisplayedRows: "{from}-{to} of {count}",
                            labelRowsSelect: "rows",
                        },
                        toolbar: {
                            nRowsSelected: "{0} row(s) selected",
                        },
                        header: {
                            actions: "Actions",
                        },
                        body: {
                            emptyDataSourceMessage: "No data to display",
                            filterRow: {
                                filterTooltip: "Filter",
                            },
                        },
                    }}
                    actions={actions}
                    onChangePage={(page) => setPage(page)}
                    onChangeRowsPerPage={(perPage) => setPerPage(perPage)}
                    components={{
                        Toolbar: (props) => (
                            <>
                                <div>
                                    <Grid container spacing={2} sx={{ px: 0.1, pb: 3.5 }}>
                                        <Grid item xs={12} sm={3}>
                                            <CardHeader
                                                sx={{ p: 0 }}
                                                title={
                                                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                                                        <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                            Organization List
                                                        </Typography>
                                                        {lastSyncedAt && <Chip size="small" label={liveSyncChipLabel(lastSyncedAt)} />}
                                                    </Box>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={9} style={{ textAlign: "right" }}>
                                            {(identity.user.role_id === 1 || identity.user.role_id === 2) && (
                                                <ButtonGroup>
                                                    <Button
                                                        onClick={() => handleOpen("create", null)}
                                                        variant="outlined"
                                                        startIcon={<Icons.AddIcon fontWeight="bold" />}
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 800,
                                                            paddingTop: 0.5,
                                                            paddingBottom: 0.5,
                                                        }}
                                                    >
                                                        Create
                                                    </Button>
                                                    {identity.user.role_id === 1 && (
                                                        <Button
                                                            onClick={() => handleOpen("import", null)}
                                                            variant="outlined"
                                                            startIcon={<Icons.Import fontWeight="bold" />}
                                                            color="primary"
                                                            sx={{
                                                                fontWeight: 800,
                                                                paddingTop: 0.5,
                                                                paddingBottom: 0.5,
                                                            }}
                                                        >
                                                            Import
                                                        </Button>
                                                    )}
                                                    <Button
                                                        onClick={() => downloadExcelFile()}
                                                        variant="outlined"
                                                        startIcon={<Icons.FileDownload fontWeight="bold" />}
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 800,
                                                            paddingTop: 0.5,
                                                            paddingBottom: 0.5,
                                                        }}
                                                    >
                                                        Export
                                                    </Button>
                                                </ButtonGroup>
                                            )}
                                        </Grid>
                                    </Grid>
                                </div>

                                {hasSelectedCountry && (identity.user.role_id === 1 || identity.user.role_id === 2) && (
                                    <Grid container columnSpacing={0.5} rowSpacing={2.5} sx={{ mb: 4.5 }}>
                                        {customerStats.map((stat) => (
                                            <Grid item xs={12} sm={6} md={3} key={stat.label}>
                                                <Box
                                                    role={stat.route ? "button" : undefined}
                                                    tabIndex={stat.route ? 0 : undefined}
                                                    onClick={() => {
                                                        if (stat.route) {
                                                            navigate(stat.route, {
                                                                state: {
                                                                    countryId: Number(filter.country_id),
                                                                },
                                                            });
                                                        }
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (!stat.route) return;
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            event.preventDefault();
                                                            navigate(stat.route, {
                                                                state: {
                                                                    countryId: Number(filter.country_id),
                                                                },
                                                            });
                                                        }
                                                    }}
                                                    sx={{
                                                        height: "100%",
                                                        minHeight: 36,
                                                        px: 1.2,
                                                        py: 0.85,
                                                        borderRadius: 1,
                                                        border: 1,
                                                        borderColor: "#e0e0e0",
                                                        backgroundColor: "#ffffff",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        cursor: stat.route ? "pointer" : "default",
                                                        transition: "all 0.15s ease",
                                                        "&:hover": stat.route
                                                            ? {
                                                                  borderColor: "primary.main",
                                                                  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.10)",
                                                              }
                                                            : undefined,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "#4a5a6a",
                                                            fontSize: "0.72rem",
                                                            letterSpacing: "0.02em",
                                                            lineHeight: 1.15,
                                                            textTransform: "uppercase",
                                                        }}
                                                    >
                                                        {stat.label}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            mt: 0.25,
                                                            color: "primary.main",
                                                            fontSize: "0.875rem",
                                                            fontWeight: 700,
                                                            lineHeight: 1.1,
                                                        }}
                                                    >
                                                        {stat.value}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}

                                <form>
                                    <Grid container spacing={2} sx={{ paddingBottom: 1.5 }}>
                                        <Grid item xs={12} sm={2.6} md={1.1} xl={1.1}>
                                            <TextField
                                                autoComplete="off"
                                                fullWidth
                                                label="Code"
                                                name="org_code"
                                                variant="outlined"
                                                autoFocus={codeInputRef.current === document.activeElement}
                                                value={filter.org_code}
                                                onChange={handleFilterChange}
                                                inputRef={codeInputRef}
                                                size="small"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    style: { textTransform: "uppercase" },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3.7} md={2.5} xl={2.4}>
                                            <TextField
                                                autoComplete="off"
                                                fullWidth
                                                label="Name"
                                                name="org_name"
                                                variant="outlined"
                                                autoFocus={nameInputRef.current === document.activeElement}
                                                value={filter.org_name}
                                                onChange={handleFilterChange}
                                                inputRef={nameInputRef}
                                                size="small"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    style: { textTransform: "uppercase" },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3.7} md={2.3} xl={2.4}>
                                            <TextField
                                                autoComplete="off"
                                                fullWidth
                                                label="Email"
                                                name="email"
                                                variant="outlined"
                                                autoFocus={emailInputRef.current === document.activeElement}
                                                value={filter.email}
                                                onChange={handleFilterChange}
                                                inputRef={emailInputRef}
                                                size="small"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={2.1} xl={2.1}>
                                            <TextField
                                                autoComplete="off"
                                                fullWidth
                                                label="Office Phone or Mobile Phone"
                                                name="office_phone"
                                                variant="outlined"
                                                autoFocus={officePhoneInputRef.current === document.activeElement}
                                                value={filter.office_phone}
                                                onChange={handleFilterChange}
                                                inputRef={officePhoneInputRef}
                                                size="small"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={1.5} xl={1.5}>
                                            <TextField
                                                autoComplete="off"
                                                fullWidth
                                                label="PIC Name"
                                                name="pic_name"
                                                variant="outlined"
                                                autoFocus={picNameInputRef.current === document.activeElement}
                                                value={filter.pic_name}
                                                onChange={handleFilterChange}
                                                inputRef={picNameInputRef}
                                                size="small"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={1.5} xl={1.5}>
                                            <CountrySelect 
                                                value={filter.country_id === undefined ? "" : filter.country_id} 
                                                onChange={handleFilterChange} 
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2} xl={2}>
                                            <Autocomplete
                                                freeSolo
                                                disableClearable
                                                size="small"
                                                open={businessTypeAutocompleteOpen}
                                                options={businessTypeData}
                                                getOptionLabel={(option) => (typeof option === "string" ? option : option.business_type || "")}
                                                inputValue={businessTypeInput}
                                                onInputChange={(_, newInputValue, reason) => {
                                                    handleBusinessTypeFilterChange(newInputValue);

                                                    if (reason === "input") {
                                                        setBusinessTypeAutocompleteOpen(true);
                                                    }

                                                    if (reason === "reset") {
                                                        setBusinessTypeAutocompleteOpen(false);
                                                    }
                                                }}
                                                onChange={(_, selectedValue) => {
                                                    const nextValue = typeof selectedValue === "string" ? selectedValue : selectedValue?.business_type || "";
                                                    handleBusinessTypeFilterChange(nextValue);
                                                    setBusinessTypeAutocompleteOpen(false);
                                                }}
                                                onClose={() => setBusinessTypeAutocompleteOpen(false)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        autoComplete="off"
                                                        fullWidth
                                                        label="Nature Of Business"
                                                        variant="outlined"
                                                        autoFocus={businessTypeInputRef.current === document.activeElement}
                                                        inputRef={businessTypeInputRef}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            type: "search",
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={8} md={9} xl={9} style={{ paddingTop: 8.5 }}>
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                name="product_id"
                                                options={productsData}
                                                groupBy={(option) => {
                                                    const country = Countries.find((country) => country.id === option.country_id);
                                                    return country ? country.name : "Unknown";
                                                }}
                                                value={filter.product_id}
                                                getOptionLabel={(option) => option.product_description}
                                                onChange={(_, selectedValue) => handleProductFilterChange(selectedValue)}
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox icon={Icons.CheckBoxBlankIcon} checkedIcon={Icons.CheckBoxCheckedIcon} style={{ marginRight: 8 }} checked={selected} />
                                                        {option.product_description}
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        margin="dense"
                                                        id="product_id"
                                                        label="Product"
                                                        fullWidth
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                )}
                                                renderGroup={(params) => {
                                                    return (
                                                        <li key={params.key}>
                                                            <GroupHeader>{params.group}</GroupHeader>
                                                            <GroupItems>{params.children}</GroupItems>
                                                        </li>
                                                    );
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={1} md={1} xl={1}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="error"
                                                sx={{
                                                    color: "#ffffff",
                                                    marginTop: "2px",
                                                }}
                                                onClick={handleResetFilter}
                                            >
                                                Reset
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </>
                        ),
                    }}
                />
                <CreateOrganizationForm open={open.create} handleClose={() => handleClose("create")} fetchData={fetchData} userCountry={userCountry} />
                <EditOrganizationForm open={open.edit} handleClose={() => handleClose("edit")} rowData={selectedRow} fetchData={fetchData} userCountry={userCountry} />
                <ViewOrganizationForm open={open.view} handleClose={() => handleClose("view")} rowData={selectedRow} />
                <ImportOrganizationForm open={open.import} handleClose={() => handleClose("import")} fetchData={fetchData} userCountry={userCountry} />
                <OrganizationContractsForm open={open.contracts} handleClose={() => handleClose("contracts")} rowData={selectedRow} />
                <OrganizationTouchpointsForm open={open.touchpoints} handleClose={() => handleClose("touchpoints")} rowData={selectedRow} />
            </Container>
        </div>
    );
};
